/**
 * useAttendanceSync — Real-time Firestore listener for Attendance Records
 *
 * Subscribes to scoped project attendance subcollection and
 * pushes incoming data into the Zustand AttendanceSlice.
 *
 * Guards: Only subscribes when cloud.enabled, cloud.orgId, cloud.projectId, and
 * auth.initialized are all truthy — same pattern as useScheduleSync.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { subscribeToAttendance } from '../../attendance/attendance-api';
import type { ActorAttendance, AttendanceRecord } from '../../attendance/types';
import {
    clearPendingAttendanceActorWrite,
    clearPendingAttendanceRecordWrite,
    isPendingAttendanceRecordWrite,
    pendingAttendanceActorIds,
} from '../attendance-pending-writes';
import type { ProjectScope } from '../project-scope';

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function recordsMatchForPendingWrite(left: unknown, right: unknown): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

function pendingRecordFields(remoteRecord: AttendanceRecord, localRecord: AttendanceRecord): AttendanceRecord {
    return {
        ...remoteRecord,
        eventDate: localRecord.eventDate,
        eventTitle: localRecord.eventTitle,
        eventStartTime: localRecord.eventStartTime,
        gracePeriodMinutes: localRecord.gracePeriodMinutes,
        coverCredits: localRecord.coverCredits,
    };
}

function hasPendingAttendanceActor(scope: ProjectScope, record: AttendanceRecord): boolean {
    return pendingAttendanceActorIds(scope, record.eventId).some((actorId) => Boolean(record.actors[actorId]));
}

export function mergeAttendanceRecordsWithPendingLocal(
    scope: ProjectScope,
    remoteRecords: AttendanceRecord[],
    localRecords: AttendanceRecord[]
): AttendanceRecord[] {
    const remoteIds = new Set(remoteRecords.map((record) => record.eventId));
    const localByEventId = new Map(localRecords.map((record) => [record.eventId, record]));
    const mergedRemoteRecords = remoteRecords.map((remoteRecord) => {
        const localRecord = localByEventId.get(remoteRecord.eventId);
        if (!localRecord) return remoteRecord;

        let mergedRecord = isPendingAttendanceRecordWrite(scope, remoteRecord.eventId)
            ? pendingRecordFields(remoteRecord, localRecord)
            : remoteRecord;
        const pendingActorIds = pendingAttendanceActorIds(scope, remoteRecord.eventId);
        const pendingActors = pendingActorIds.reduce<Record<string, ActorAttendance>>((actors, actorId) => {
            const localActor = localRecord.actors[actorId];
            if (localActor) {
                actors[actorId] = localActor;
            }
            return actors;
        }, {});

        if (Object.keys(pendingActors).length > 0) {
            mergedRecord = {
                ...mergedRecord,
                actors: {
                    ...mergedRecord.actors,
                    ...pendingActors,
                },
            };
        }

        return mergedRecord;
    });
    const pendingLocalRecords = localRecords.filter((record) => (
        !remoteIds.has(record.eventId)
        && (isPendingAttendanceRecordWrite(scope, record.eventId) || hasPendingAttendanceActor(scope, record))
    ));

    return [...mergedRemoteRecords, ...pendingLocalRecords];
}

function recordPendingFieldsMatch(remoteRecord: AttendanceRecord, localRecord: AttendanceRecord): boolean {
    return remoteRecord.eventDate === localRecord.eventDate
        && remoteRecord.eventTitle === localRecord.eventTitle
        && remoteRecord.eventStartTime === localRecord.eventStartTime
        && remoteRecord.gracePeriodMinutes === localRecord.gracePeriodMinutes
        && recordsMatchForPendingWrite(
            remoteRecord.coverCredits ?? [],
            localRecord.coverCredits ?? []
        );
}

function clearAcknowledgedAttendanceWrites(
    scope: ProjectScope,
    remoteRecords: AttendanceRecord[],
    localRecords: AttendanceRecord[]
): void {
    const localByEventId = new Map(localRecords.map((record) => [record.eventId, record]));
    for (const remoteRecord of remoteRecords) {
        const localRecord = localByEventId.get(remoteRecord.eventId);
        if (!localRecord || recordPendingFieldsMatch(remoteRecord, localRecord)) {
            clearPendingAttendanceRecordWrite(scope, remoteRecord.eventId);
        }

        for (const actorId of pendingAttendanceActorIds(scope, remoteRecord.eventId)) {
            const remoteActor = remoteRecord.actors[actorId];
            const localActor = localRecord?.actors[actorId];
            if (!localActor || recordsMatchForPendingWrite(remoteActor, localActor)) {
                clearPendingAttendanceActorWrite(scope, remoteRecord.eventId, actorId);
            }
        }
    }
}

export function useAttendanceSync(enabled = true) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const unsubRef = useRef<(() => void) | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        // Tear down previous subscription on any dependency change
        if (unsubRef.current) {
            unsubRef.current();
            unsubRef.current = null;
        }

        // Guard: Only subscribe when all conditions are met
        if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
            return;
        }

        // Subscribe to attendance records
        const scope = { orgId, projectId };
        unsubRef.current = subscribeToAttendance(scope, (records) => {
            if (!mountedRef.current) return;
            useAppStore.setState(state => ({
                attendanceRecords: mergeAttendanceRecordsWithPendingLocal(scope, records, state.attendanceRecords || []),
            }));
            clearAcknowledgedAttendanceWrites(scope, records, useAppStore.getState().attendanceRecords || []);
        });

        return () => {
            if (unsubRef.current) {
                unsubRef.current();
                unsubRef.current = null;
            }
        };
    }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user]);
}
